import { Navigate, Route } from 'react-router-dom';

import { ResponsiveLayout } from '@/components/widgets/t/ResponsiveLayout/ResponsiveLayout.layout';
import { LandstewardInvestmentsPage } from '@/pages/landsteward/pages/investments/Investments.page';
import { LandstewardEditPlotPage } from '@/pages/landsteward/pages/plot/pages/edit/edit-plot.page';
import { LandstewardNewPlotPage } from '@/pages/landsteward/pages/plot/pages/new/new-plot.page';
import { LandstewardPortfolioPage } from '@/pages/landsteward/pages/portfolio/Portfolio.page';
import { ProjectsNewPage } from '@/pages/landsteward/pages/projects/pages/new-project/NewProject.page';
import { RestrictedAccessPage } from '@/pages/restricted-access/RestrictedAccess.page';
import { AccountEditEmailPage } from '@/pages/shared/account/pages/edit-email/EditEmail.page';
import { AccountEditNamePage } from '@/pages/shared/account/pages/edit-name/EditName.page';
import { AccountEditPasswordPage } from '@/pages/shared/account/pages/edit-password/EditPassword.page';
import { AccountPage } from '@/pages/shared/account/pages/index/Account.page';
import { AccountLegalPage } from '@/pages/shared/account/pages/legal/AccountLegal.page';
import { AccountTeamPage } from '@/pages/shared/account/pages/team/AccountTeam.page';
import { AccountTeamEditMemberPage } from '@/pages/shared/account/pages/team/pages/edit-member/AccountTeamEditMember.page';
import { AccountTeamInvitePage } from '@/pages/shared/account/pages/team/pages/invite/AccountTeamInvite.page';
import { ComparePage } from '@/pages/shared/compare/Compare.page';
import { CompareListPage } from '@/pages/shared/compare/CompareList.page';
import { ComplianceFrameworksPage } from '@/pages/shared/compliance-frameworks/ComplianceFrameworks.page';
import { CopilotPage } from '@/pages/shared/copilot/Copilot.page';
import { DashboardPage } from '@/pages/shared/dashboard/Dashboard.page';
import { GoalsPage } from '@/pages/shared/goals/Goals.page';
import { RoadmapPage } from '@/pages/shared/goals/pages/Roadmap.page';
import { IndicatorsPage } from '@/pages/shared/indicators/Indicators.page';
import { KnowledgePage } from '@/pages/shared/knowledge/Knowledge.page';
import { LandBiodiversityPage } from '@/pages/shared/land/LandBiodiversity.page';
import { LandCarbonPage } from '@/pages/shared/land/LandCarbon.page';
import { LandSocialPage } from '@/pages/shared/land/LandSocial.page';
import { LandSoilPage } from '@/pages/shared/land/LandSoil.page';
import { LandWaterPage } from '@/pages/shared/land/LandWater.page';
import { MediaUploadPage } from '@/pages/shared/media-upload/MediaUpload.page';
import { PlotOverviewPage } from '@/pages/shared/media-upload/pages/PlotOverview.page';
import { OrganizationOnboardingPage } from '@/pages/shared/organization/pages/Onboarding.page';
import { InteractiveMapPage } from '@/pages/shared/plots/pages/InteractiveMap.page';
import { PlotGroupsPage } from '@/pages/shared/plots/PlotGroups.page';
import { PlotsPage } from '@/pages/shared/plots/Plots.page';
import { ContractsPage } from '@/pages/shared/portfolio/pages/contracts/Contracts.page';
import { PortfolioNaturalCapitalPage } from '@/pages/shared/portfolio/pages/NaturalCapital/PortfolioNaturalCapital.page';
import { StocksPage } from '@/pages/shared/portfolio/pages/stocks/Stocks.page';
import { UpliftUnitsPage } from '@/pages/shared/portfolio/pages/uplift/UpliftUnits.page';
import { PortfolioForecastsPage } from '@/pages/shared/portfolio/PortfolioForecasts.page';
import { PortfolioInvestmentsPage } from '@/pages/shared/portfolio/PortfolioInvestments.page';
import { DetailsPage } from '@/pages/shared/projects/pages/project/Details.page';
import { NDVIDetailPage } from '@/pages/shared/projects/pages/project/pages/conservation/restoration/pages/ndvi/NDVIDetail.page';
import { InteractiveMap } from '@/pages/shared/projects/pages/project/pages/conservation/shared/pages/interactive-map/interactive-map.page';
import { DeforestationDetailPage } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/deforestation/DeforestationDetail.page';
import { ForestStructureDetailPage } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/forestStructure/ForestStructureDetail.page';
import { ProjectPage } from '@/pages/shared/projects/pages/project/Project.page';
import { ProjectsPage } from '@/pages/shared/projects/Projects.page';
import { ReportsPage } from '@/pages/shared/reports/Reports.page';
import { OrganizationsPage } from '@/pages/shared/staff-portal/pages/organizations/Organizations.page';
import {
  EcologicalIntegrityPages,
  HabitatIntactnessPages,
  IndicatorSpeciesPages,
} from '@/routing/components/ConservationPages';
import { WaterDetailPages } from '@/routing/components/ProjectTypePages';
import { HomePageProtection } from '@/routing/components/protections/home-page/HomePageProtection';
import {
  RequireAccessiblePlot,
  RequireAccessiblePlotWithoutProject,
} from '@/routing/components/protections/require-accessible-plot/RequireAccessiblePlot';
import { RequireAccessibleProject } from '@/routing/components/protections/require-accessible-project/RequireAccessibleProject';
import { RequireAdminRole } from '@/routing/components/protections/require-admin-role/RequireAdminRole';
import { ConservationProtection } from '@/routing/components/protections/require-conservation/ConservationProtection';
import { NoGroupByProtection } from '@/routing/components/protections/require-group-by/NoGroupByProtection';
import { RequireSupportPlot } from '@/routing/components/protections/require-support-plot/RequireSupportPlot';
import { paths } from '@/routing/routing';

import { AccountPageWrapper } from '../../account/components/Account.page-wrapper';
import { ComplianceFrameworksPageWrapper } from '../../compliance-frameworks/components/ComplianceFrameworks.page-wrapper';
import { GlobalCopilotPage } from '../../copilot/GlobalCopilot.page';
import { PlotPage } from '../../projects/pages/project/pages/sustainableProduction/pages/plot/Plot.page';
import { OrganizationPage } from '../pages/organization/Organization.page';
import { StaffSignedInRoute } from './components/StaffSignedInRoute';

export const StaffPortalRoutes = (
  <Route path='staff'>
    <Route element={<StaffSignedInRoute />}>
      <Route index element={<Navigate to={paths.staff.organizations} />} />
      <Route path={paths.staff.organizations} element={<OrganizationsPage />} />
      <Route path={paths.staff.globalCopilot} element={<GlobalCopilotPage />} />
      <Route element={<ResponsiveLayout showImpersonatingBanner={false} />}>
        <Route path={paths.staff.account} element={<AccountPageWrapper />}>
          <Route index element={<AccountPage />} />
          <Route path={paths.staff.accountEditEmail} element={<AccountEditEmailPage />} />
          <Route path={paths.staff.accountEditName} element={<AccountEditNamePage />} />
          <Route path={paths.staff.accountEditPassword} element={<AccountEditPasswordPage />} />
          <Route path={paths.staff.accountLegal} element={<AccountLegalPage />} />
          <Route path={paths.staff.accountTeam}>
            <Route index element={<AccountTeamPage />} />
            <Route element={<RequireAdminRole />}>
              <Route path={paths.staff.accountTeamEditMember} element={<AccountTeamEditMemberPage />} />
              <Route path={paths.staff.accountTeamInvite} element={<AccountTeamInvitePage />} />
            </Route>
          </Route>
        </Route>

        <Route path={paths.staff.socialDetails} element={<LandSocialPage />} />

        <Route path={paths.staff.portfolio} element={<LandstewardPortfolioPage />} />

        <Route path={paths.staff.portfolioNaturalCapital} element={<PortfolioNaturalCapitalPage />} />
        <Route path={paths.staff.portfolioForecasts} element={<PortfolioForecastsPage />} />
        <Route path={paths.staff.portfolioInvestments} element={<PortfolioInvestmentsPage />} />
        <Route path={paths.staff.portfolioUnits} element={<UpliftUnitsPage />} />
        <Route path={paths.staff.portfolioStocks} element={<StocksPage />} />
        <Route path={paths.staff.portfolioContracts} element={<ContractsPage />} />

        <Route path={paths.staff.investments} element={<LandstewardInvestmentsPage />} />
        <Route element={<RequireSupportPlot />}>
          <Route path={paths.staff.plots} element={<PlotsPage />} />
          <Route element={<NoGroupByProtection />}>
            <Route path={paths.staff.groups} element={<PlotGroupsPage />} />
            <Route path={paths.staff.comparisons} element={<CompareListPage />} />
            <Route path={paths.staff.compare} element={<ComparePage />} />
          </Route>
        </Route>
        <Route index element={<Navigate to={paths.staff.projects} replace />} />

        <Route element={<NoGroupByProtection />}>
          <Route path={`${paths.staff.details}/*`} element={<DetailsPage />} />
          <Route path={paths.staff.biodiversityDetails} element={<LandBiodiversityPage />} />
          <Route path={paths.staff.soilDetails} element={<LandSoilPage />} />
          <Route path={paths.staff.carbonDetails} element={<LandCarbonPage />} />
          <Route path={paths.staff.waterDetails} element={<LandWaterPage />} />
          <Route path={paths.staff.deforestationDetails} element={<DeforestationDetailPage />} />
          <Route path={paths.staff.forestDetails} element={<ForestStructureDetailPage />} />
        </Route>

        <Route path={paths.staff.projects}>
          <Route path={paths.staff.newProject} element={<ProjectsNewPage />} />
          <Route index element={<ProjectsPage />} />
          <Route path={paths.staff.projectsComplianceFrameworks} element={<ComplianceFrameworksPageWrapper />}>
            <Route index element={<ComplianceFrameworksPage />} />
          </Route>
          <Route element={<RequireAccessibleProject />}>
            <Route path={paths.staff.ecologicalIntegrity} element={<EcologicalIntegrityPages />} />
            <Route path={paths.staff.habitatIntactness} element={<HabitatIntactnessPages />} />
            <Route path={paths.staff.indicatorSpecies} element={<IndicatorSpeciesPages />} />
            <Route path={paths.staff.ndvi} element={<NDVIDetailPage />} />
            <Route path={`${paths.staff.projectDetails}/*`} element={<ProjectPage />} />
            <Route path={paths.staff.interactiveMap} element={<InteractiveMap />} />
            <Route path={paths.staff.projectDetailsWater} element={<WaterDetailPages />} />

            <Route element={<RequireAccessiblePlot />}>
              <Route path={`${paths.staff.plot}/*`} element={<PlotPage />} />
            </Route>
            <Route path={`${paths.staff.editPlot}/*`} element={<LandstewardEditPlotPage />} />
            <Route path={`${paths.staff.newPlot}/*`} element={<LandstewardNewPlotPage />} />
          </Route>
        </Route>
        <Route element={<RequireAccessiblePlotWithoutProject />}>
          <Route path={paths.staff.plotInteractiveMap} element={<InteractiveMapPage />} />
        </Route>

        <Route path={paths.staff.restrictedAccess} element={<RestrictedAccessPage />} />
        <Route path={paths.staff.indicators} element={<IndicatorsPage />} />
        <Route element={<HomePageProtection />}>
          <Route path={paths.staff.dashboard} element={<DashboardPage />} />
        </Route>

        <Route path={paths.staff.contracts} element={<ContractsPage />} />
        <Route path={paths.staff.reports} element={<ReportsPage />} />

        <Route path={paths.staff.goals} element={<GoalsPage />} />
        <Route path={paths.staff.roadmap} element={<RoadmapPage />} />

        <Route path={paths.staff.copilot} element={<CopilotPage />} />
        <Route path={paths.staff.knowledge} element={<KnowledgePage />} />
        <Route element={<ConservationProtection />}>
          <Route path={paths.staff.mediaUpload} element={<MediaUploadPage />} />
          <Route path={paths.staff.mediaUploadPlotOverview} element={<PlotOverviewPage />} />
        </Route>
        <Route path={paths.staff.organization} element={<OrganizationPage />} />
      </Route>
      <Route path={paths.staff.organizationOnboarding} element={<OrganizationOnboardingPage />} />
    </Route>
  </Route>
);
